import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Upload,
  Image,
  Users,
  BarChart3,
  Calendar,
  MapPin,
  Camera,
  Clock,
  Info,
  RefreshCw,
  Scissors,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

import { useEvent, useDeleteEvent } from '@/api/hooks/useEvents';
import { EventStatusBadge } from '@/components/events/EventStatusBadge';
import { EventFacesTab } from '@/components/faces/EventFacesTab';
import type { EventPhase } from '@/api/types/event.types';

type TabId = 'overview' | 'upload' | 'gallery' | 'faces' | 'analysis';

const TABS: Array<{ id: TabId; iconKey: string }> = [
  { id: 'overview', iconKey: 'overview' },
  { id: 'upload', iconKey: 'upload' },
  { id: 'gallery', iconKey: 'gallery' },
  { id: 'faces', iconKey: 'faces' },
  { id: 'analysis', iconKey: 'analysis' },
];

const TAB_ICONS: Record<TabId, typeof Info> = {
  overview: Info,
  upload: Upload,
  gallery: Image,
  faces: Users,
  analysis: BarChart3,
};

const PHASE_TYPE_LABELS: Record<string, string> = {
  preparation_bride: 'events.phases.preparationBride',
  preparation_groom: 'events.phases.preparationGroom',
  pre_wedding_outdoor: 'events.phases.preWeddingOutdoor',
  ceremony: 'events.phases.ceremony',
  couple_session: 'events.phases.coupleSession',
  family_portraits: 'events.phases.familyPortraits',
  cocktail: 'events.phases.cocktail',
  reception_entrance: 'events.phases.receptionEntrance',
  first_dance: 'events.phases.firstDance',
  dinner: 'events.phases.dinner',
  dancing: 'events.phases.dancing',
  cake_cutting: 'events.phases.cakeCutting',
  other: 'events.phases.other',
};

export function EventDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: event, isLoading, isError, refetch, isFetching } = useEvent(id || '');

  // Auto-refresh every 5 seconds while processing is active
  const isProcessing = event && event.processed_count < event.photo_count && event.photo_count > 0;
  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(() => refetch(), 5000);
    return () => clearInterval(interval);
  }, [isProcessing, refetch]);
  const deleteEvent = useDeleteEvent();

  const handleDelete = () => {
    if (id) {
      deleteEvent.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="flex justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      </div>
    );
  }

  if (isError || !event) {
    return (
      <div className="page-container">
        <div className="card text-center">
          <p className="text-sm text-red-600 dark:text-red-400">{t('events.loadError')}</p>
          <Link to="/events" className="btn-secondary mt-4">
            {t('actions.back')}
          </Link>
        </div>
      </div>
    );
  }

  const formattedDate = (() => {
    try {
      return format(parseISO(event.event_date), 'PPP');
    } catch {
      return event.event_date;
    }
  })();

  return (
    <div className="page-container">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/events"
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="page-title">{event.title}</h1>
            {event.partners_display && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {event.partners_display}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <EventStatusBadge status={event.processing_status} />
          <Link
            to={`/events/${id}/edit`}
            className="btn-secondary inline-flex items-center gap-2"
          >
            <Edit className="h-4 w-4" />
            {t('actions.edit')}
          </Link>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="btn-danger inline-flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {t('actions.delete')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6 overflow-x-auto" aria-label="Tabs">
          {TABS.map((tab) => {
            const Icon = TAB_ICONS[tab.id];
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-500 dark:hover:text-gray-200'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t(`events.tabs.${tab.id}`)}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'overview' && <OverviewTab event={event} t={t} formattedDate={formattedDate} onRefresh={refetch} isRefreshing={isFetching} />}
        {activeTab === 'upload' && <PlaceholderTab icon={Upload} tabKey="upload" t={t} eventId={id!} />}
        {activeTab === 'gallery' && <PlaceholderTab icon={Image} tabKey="gallery" t={t} eventId={id!} />}
        {activeTab === 'faces' && <EventFacesTab eventId={id!} />}
        {activeTab === 'analysis' && <PlaceholderTab icon={BarChart3} tabKey="analysis" t={t} eventId={id!} />}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="card max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('events.deleteConfirmTitle')}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t('events.deleteConfirmDescription', { title: event.title })}
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-secondary"
              >
                {t('actions.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteEvent.isPending}
                className="btn-danger"
              >
                {deleteEvent.isPending ? t('status.loading') : t('actions.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview Tab
// ---------------------------------------------------------------------------

function OverviewTab({
  event,
  t,
  formattedDate,
  onRefresh,
  isRefreshing,
}: {
  event: NonNullable<ReturnType<typeof useEvent>['data']>;
  t: ReturnType<typeof useTranslation>['t'];
  formattedDate: string;
  onRefresh: () => void;
  isRefreshing: boolean;
}) {
  return (
    <div className="space-y-6">
      {/* Stats Header with Refresh */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('events.stats.title')}</h3>
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="btn-secondary inline-flex items-center gap-2 text-sm"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {t('actions.refresh')}
        </button>
      </div>

      {/* Processing Progress Bar */}
      {event.photo_count > 0 && (
        <ProcessingProgress
          processedCount={event.processed_count}
          totalCount={event.photo_count}
          progress={event.processing_progress}
          isRefreshing={isRefreshing}
          t={t}
        />
      )}

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Camera}
          label={t('events.stats.totalPhotos')}
          value={event.photo_count.toLocaleString()}
        />
        <StatCard
          icon={BarChart3}
          label={t('events.stats.processed')}
          value={`${event.processed_count.toLocaleString()} / ${event.photo_count.toLocaleString()}`}
          subtitle={`${event.processing_progress}%`}
        />
        <StatCard
          icon={Users}
          label={t('events.stats.faceClusters')}
          value={event.face_cluster_count.toLocaleString()}
        />
        <StatCard
          icon={Clock}
          label={t('events.stats.phases')}
          value={event.phases.length.toString()}
        />
      </div>

      {/* Event Details */}
      <div className="card">
        <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100">
          {t('events.eventDetails')}
        </h3>
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('events.fields.eventDate')}
            </dt>
            <dd className="mt-1 flex items-center gap-1.5 text-sm text-gray-900 dark:text-gray-100">
              <Calendar className="h-4 w-4 text-gray-400" />
              {formattedDate}
            </dd>
          </div>
          {event.venue_name && (
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('events.fields.venueName')}
              </dt>
              <dd className="mt-1 flex items-center gap-1.5 text-sm text-gray-900 dark:text-gray-100">
                <MapPin className="h-4 w-4 text-gray-400" />
                {event.venue_name}
              </dd>
            </div>
          )}
          {event.partners_display && (
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('events.fields.partners')}
              </dt>
              <dd className="mt-1 flex items-center gap-1.5 text-sm text-gray-900 dark:text-gray-100">
                <Users className="h-4 w-4 text-gray-400" />
                {event.partners_display}
              </dd>
            </div>
          )}
          {event.created_by_email && (
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('events.fields.createdBy')}
              </dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                {event.created_by_email}
              </dd>
            </div>
          )}
        </dl>
        {event.description && (
          <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('events.fields.description')}
            </dt>
            <dd className="mt-1 whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">
              {event.description}
            </dd>
          </div>
        )}
      </div>

      {/* Phases Timeline */}
      {event.phases.length > 0 && (
        <div className="card">
          <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100">
            {t('events.phasesTimeline')}
          </h3>
          <div className="space-y-3">
            {event.phases.map((phase) => (
              <PhaseTimelineItem key={phase.id} phase={phase} t={t} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------

function StatCard({
  icon: Icon,
  label,
  value,
  subtitle,
}: {
  icon: typeof Camera;
  label: string;
  value: string;
  subtitle?: string;
}) {
  return (
    <div className="card flex items-center gap-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
        <Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {label}
          {subtitle && <span className="ms-1 text-primary-600 dark:text-primary-400">({subtitle})</span>}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase Timeline Item
// ---------------------------------------------------------------------------

function PhaseTimelineItem({
  phase,
  t,
}: {
  phase: EventPhase;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const labelKey = PHASE_TYPE_LABELS[phase.phase_type];
  const displayLabel = phase.label || (labelKey ? t(labelKey) : phase.phase_type);

  const formatTime = (iso: string) => {
    try {
      return format(parseISO(iso), 'HH:mm');
    } catch {
      return iso;
    }
  };

  return (
    <div className="flex items-center gap-4 rounded-lg border border-gray-100 p-3 dark:border-gray-700">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
        {phase.sort_order + 1}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{displayLabel}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatTime(phase.start_time)} - {formatTime(phase.end_time)}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Processing Progress
// ---------------------------------------------------------------------------

function ProcessingProgress({
  processedCount,
  totalCount,
  progress,
  isRefreshing,
  t,
}: {
  processedCount: number;
  totalCount: number;
  progress: number;
  isRefreshing: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const prevCountRef = useRef(processedCount);
  const prevTimeRef = useRef(Date.now());
  const [eta, setEta] = useState<string>('');

  const isComplete = processedCount >= totalCount;
  const remaining = totalCount - processedCount;

  useEffect(() => {
    const prevCount = prevCountRef.current;
    const prevTime = prevTimeRef.current;
    const now = Date.now();
    const countDiff = processedCount - prevCount;
    const timeDiff = (now - prevTime) / 1000;

    if (countDiff > 0 && timeDiff > 0 && remaining > 0) {
      const rate = countDiff / timeDiff;
      const etaSeconds = remaining / rate;
      if (etaSeconds < 60) {
        setEta(t('events.stats.etaSeconds', { seconds: Math.ceil(etaSeconds) }));
      } else if (etaSeconds < 3600) {
        setEta(t('events.stats.etaMinutes', { minutes: Math.ceil(etaSeconds / 60) }));
      } else {
        const hours = Math.floor(etaSeconds / 3600);
        const mins = Math.ceil((etaSeconds % 3600) / 60);
        setEta(t('events.stats.etaHoursMinutes', { hours, minutes: mins }));
      }
    }

    prevCountRef.current = processedCount;
    prevTimeRef.current = now;
  }, [processedCount, remaining, t]);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {isComplete
            ? t('events.stats.processingComplete')
            : t('events.stats.processingActive')}
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {processedCount.toLocaleString()} / {totalCount.toLocaleString()}
          {isRefreshing && <RefreshCw className="inline-block ms-2 h-3 w-3 animate-spin text-gray-400" />}
        </span>
      </div>
      <div className="w-full h-3 bg-gray-200 rounded-full dark:bg-gray-700 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            isComplete
              ? 'bg-green-500'
              : 'bg-primary-500 animate-pulse'
          }`}
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {progress}%
        </span>
        {!isComplete && eta && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {eta}
          </span>
        )}
        {!isComplete && !eta && remaining > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('events.stats.remaining', { count: remaining })}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Placeholder Tab
// ---------------------------------------------------------------------------

function PlaceholderTab({
  icon: Icon,
  tabKey,
  t,
  eventId,
}: {
  icon: typeof Upload;
  tabKey: string;
  t: ReturnType<typeof useTranslation>['t'];
  eventId: string;
}) {
  const linkMap: Record<string, string> = {
    upload: '/upload',
    gallery: '/gallery',
    faces: '/faces',
    analysis: '/analysis',
  };

  const link = linkMap[tabKey];

  return (
    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 py-16 dark:border-gray-600">
      <Icon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
      <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">
        {t(`events.tabs.${tabKey}`)}
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {t('events.tabComingSoon')}
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-3">
        {link && (
          <Link to={`${link}?event=${eventId}`} className="btn-primary">
            {t(`events.goTo${tabKey.charAt(0).toUpperCase() + tabKey.slice(1)}`)}
          </Link>
        )}
        {tabKey === 'gallery' && (
          <Link
            to={`/culling?event=${eventId}`}
            className="btn-secondary inline-flex items-center gap-2"
          >
            <Scissors className="h-4 w-4" />
            {t('nav.culling')}
          </Link>
        )}
      </div>
    </div>
  );
}
